<template>
  <section
    ref="trigger"
    class="hero">
    <div class="hero-container">
      <div class="hero-content-wrapper">
        <div class="gradient1" />
        <div class="gradient2" />

        <div class="hero-content">
          <div
            :id="headlineId"
            class="headline"
            :class="data.big_title ? 'big-title' : ''"
            v-html="data.hero_headline"></div>
        </div>
        <div class="hero-image bg-image">
          <img
            ref="heroImg"
            fetchpriority="high"
            :src="data.hero_image.url"
            :alt="data.hero_image.alt"
            :title="data.hero_image.alt" />
        </div>
      </div>
    </div>

    <div
      ref="iconScroll"
      class="icon-scroll"
      @click="scrollToHash(data.section_under_hero)">
      <Arrow />
    </div>
  </section>
</template>

<script setup>
/* #META
  {
  "NAME": "HeroComponent",
  "CLASSNAME": "hero",
  "URL": "http://local.bicara.com/"
  }
  #ENDMETA */
import { ref, onBeforeUnmount, onMounted } from 'vue'
import useIntroShown from '@/composables/useIntroShown.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Arrow from '../utility/Arrow.vue'
import SplitType from 'split-type'
import useUtils from '@/composables/useUtils'
import locoScroll from '../../assets/js/LenisScroll'

const scrollToHash = (section) => {
  const el = document.getElementById(section)
  if (el) {
    locoScroll.scrollTo(`#${section}`, { immediate: false, duration: 1.2 })
  }
}

gsap.registerPlugin(ScrollTrigger)

defineProps(['data'])
const { genUUID } = useUtils()
const headlineId = ref(`headline-${genUUID()}`)

const { introShown } = useIntroShown()

const hasIntroShown = () => {
  if (introShown.value) {
    new SplitType(`#${headlineId.value} h1`, {
      types: 'lines, words',
      tagName: 'span',
    })

    gsap.fromTo(
      `#${headlineId.value} h1 .word`,
      {
        y: '100%',
      },
      {
        delay: 1.5,
        duration: 1,
        y: 0,
        ease: 'power1.out',
        stagger: 0.1,
      }
    )
    gsap.fromTo(
      `#${headlineId.value} h1 .word`,
      {
        opacity: 0.3,
      },
      {
        delay: 2,
        opacity: 1,
        stagger: 0.1,
      }
    )

    gsap.fromTo(
      ['.hero-content-wrapper'],
      {
        opacity: 0,
      },
      {
        delay: 0.5,
        duration: 1,
        opacity: 1,
        ease: 'power1.inOut',
      }
    )

    gsap.fromTo(
      `#${headlineId.value} p`,
      {
        opacity: 0,
      },
      {
        delay: 3,
        opacity: 1,
        duration: 1,
        ease: 'power1.inOut',
      }
    )
  } else {
    setTimeout(() => {
      hasIntroShown()
    }, 100)
  }
}

onMounted(() => {
  hasIntroShown()
})

onBeforeUnmount(() => {})
</script>

<style lang="scss">
.hero {
  @apply relative mb-10 px-xs lg:mb-14 lg:px-6;

  .hero-container {
    @apply container max-w-[1390px] overflow-hidden rounded-2xl lg:px-xl;

    .hero-content-wrapper {
      @apply flex min-h-[80vh] items-center justify-center px-xl py-[80px] lg:items-end lg:justify-start;
      background: radial-gradient(
        111.73% 153.51% at 49.96% 3.66%,
        rgba(183, 244, 240, 0.6) 51.6%,
        rgba(255, 248, 86, 0.12) 100%
      );
      @apply overflow-hidden px-lg;

      .gradient1 {
        @apply pointer-events-none absolute -bottom-full -left-full right-0 top-0 z-3 opacity-60;

        background: rgb(255, 245, 161);
        background: radial-gradient(
          circle,
          rgba(255, 245, 161, 1) 0%,
          rgba(183, 244, 240, 1) 43%,
          rgba(183, 244, 240, 1) 44%,
          rgba(183, 244, 240, 0) 60%
        );
      }

      .gradient2 {
        @apply pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-2 opacity-40;

        background: rgb(244, 243, 237);
        background: linear-gradient(
          90deg,
          rgba(244, 243, 237, 1) 0%,
          rgba(244, 243, 237, 1) 60%,
          rgba(244, 243, 237, 0) 100%
        );
      }

      .hero-content {
        @apply relative z-5 w-full lg:w-[70%];

        .headline {
          &.big-title {
            h1 {
              @apply header-1;
              .line {
                @apply h-[105%] overflow-hidden lg:h-[84px];
              }
            }
          }

          h1 {
            @apply header-2 mb-10 text-bicara-dark-blue;
            .line {
              @apply h-[47px] overflow-hidden lg:h-[72px];
            }

            strong {
              @apply font-light text-bicara-blue;
            }
          }

          p {
            @apply body-md text-bicara-dark-blue lg:ml-0 lg:mr-auto lg:max-w-[540px];
          }
        }
      }

      .hero-image {
        @apply absolute right-11 top-0 w-[42%] -translate-y-10;

        &.bg-image {
          @apply absolute left-0 top-0 h-full w-full transform-none overflow-hidden rounded-2xl;
        }

        img {
          @apply h-full w-full object-cover;

          object-position: 75% 0%;
        }
      }
    }
  }
  .icon-scroll {
    @apply absolute bottom-0 left-1/2 z-4 flex h-[70px] w-[70px] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-[50%] border-[1px] border-bicara-dark-blue lg:h-[96px] lg:w-[96px];
  }
}
</style>
